// shared/src/oracle/macro-block.ts
// The FRED macro block — the 30-field computed.macro FRED sub-block, extracted
// VERBATIM from the legacy refresh-fetcher builder (fetchers.ts `buildFredSlice`) so the
// oracle can become the sole macro producer (universal-source flip §3, P1b)
// without changing a single number the LLM prompts consume. PURE: takes the
// parsed FRED observations per series → the derived block. The oracle-worker macro
// fire fetches the 19 series (legacy limits) and calls this; refresh then reads the
// oracle block instead of fetching FRED itself (P2). Derivation helpers (lat/prv/
// prvValid/yoy) + every field assignment are byte-identical to the legacy code.

/** One FRED observation (sorted desc = newest first, as the legacy fetch requests). */
export interface FredObs { readonly date: string; readonly value: number | null }
/** series_id → observations (newest-first). */
export type FredSeriesMap = Readonly<Record<string, ReadonlyArray<FredObs>>>;

/** The 19 series + per-series observation limits — the legacy `specs` (15) + `extras`
 *  (4) merged. Single source of truth for the oracle macro fetch AND the block
 *  derivation. Order is not load-bearing (the block reads series by id). */
export const FRED_MACRO_BLOCK_SPECS: ReadonlyArray<readonly [string, number]> = [
  ['FEDFUNDS',     2], ['DGS10',        5], ['DGS2',         2], ['T10Y2Y',     2],
  ['CPIAUCSL',    13], ['CPILFESL',    13], ['UNRATE',       2], ['GDP',        2],
  ['UMCSENT',      2], ['WALCL',        2], ['T10YIEM',      2], ['BAMLH0A0HYM2', 2],
  ['ICSA',         2], ['PCEPILFE',    13], ['M2SL',        13],
  ['DFII10',       5], ['DGS30',        5], ['DSWP30',       5], ['DCOILBRENTEU', 5],
];

/** The 19 series ids (derived from the specs). */
export const FRED_MACRO_BLOCK_SERIES: ReadonlyArray<string> =
  FRED_MACRO_BLOCK_SPECS.map(([id]) => id);

/** Block FIELD → the ONE FRED series id it derives from (the inverse of buildFredMacroBlock's
 *  field assignments below; 1:1 field→series, N:1 block→series). Used by #79 to join the macro
 *  fire's per-series authoritative resolution onto each series ROW's display provenance. A field
 *  NOT here is a Group-C (single-source) field → NULL provenance. MUST mirror buildFredMacroBlock:
 *  the key set is guard-tested (`Object.keys(buildFredMacroBlock({}))` === `Object.keys(this)`), so
 *  adding/removing a FRED field there without updating this map fails the drift test. */
export const FRED_FIELD_SERIES: Readonly<Record<string, string>> = {
  fed_funds_rate:          'FEDFUNDS',
  fed_funds_rate_prev:     'FEDFUNDS',
  yield_10y_fred:          'DGS10',
  yield_10y_chg_bp:        'DGS10',
  yield_2y:                'DGS2',
  yield_spread_2s10s:      'T10Y2Y',
  yield_curve:             'T10Y2Y',
  cpi_yoy:                 'CPIAUCSL',
  cpi_latest:              'CPIAUCSL',
  core_cpi_yoy:            'CPILFESL',
  unemployment:            'UNRATE',
  unemployment_prev:       'UNRATE',
  gdp_level_b:             'GDP',
  gdp_qoq:                 'GDP',
  consumer_sentiment:      'UMCSENT',
  fed_balance_sheet_b:     'WALCL',
  fed_balance_chg_b:       'WALCL',
  breakeven_inflation_10y: 'T10YIEM',
  hy_spread_bps:           'BAMLH0A0HYM2',
  hy_spread_chg_bps:       'BAMLH0A0HYM2',
  hy_spread_regime:        'BAMLH0A0HYM2',
  initial_claims:          'ICSA',
  initial_claims_prev:     'ICSA',
  core_pce_yoy:            'PCEPILFE',
  m2_yoy_pct:              'M2SL',
  m2_latest_b:             'M2SL',
  tips_10y_real_yield:     'DFII10',
  yield_30y:               'DGS30',
  swap_30y:                'DSWP30',
  brent_crude:             'DCOILBRENTEU',
};

type Obs = ReadonlyArray<{ readonly value: number | null }>;
const lat = (obs: Obs): number | null => (obs.length ? obs[0]!.value : null);
const prv = (obs: Obs): number | null => (obs.length > 1 ? obs[1]!.value : null);
const prvValid = (obs: Obs): number | null => {
  for (let i = 1; i < obs.length; i++) if (obs[i]!.value != null) return obs[i]!.value;
  return null;
};
const yoy = (obs: Obs): number | null => {
  if (obs.length < 13) return null;
  const c = obs[0]!.value, a = obs[12]!.value;
  return (c != null && a != null && a !== 0) ? +(((c - a) / a) * 100).toFixed(2) : null;
};

/**
 * Build the 30-field FRED macro block from the fetched series map. Verbatim port
 * of fetchers.ts `buildFredSlice` — every field name, derivation, rounding, and regime
 * bucket is identical, so a consumer reading this block gets byte-for-byte the
 * same `computed.macro` FRED fields it does today. A missing series → its fields
 * are null (same as a failed legacy fetch), never fabricated.
 */
export function buildFredMacroBlock(series: FredSeriesMap): Record<string, unknown> {
  const g = (id: string): Obs => series[id] ?? [];
  const fed = g('FEDFUNDS'), d10 = g('DGS10'), d2 = g('DGS2'), sp = g('T10Y2Y'),
        cpi = g('CPIAUCSL'), ccpi = g('CPILFESL'), unemp = g('UNRATE'), gdp = g('GDP'),
        sent = g('UMCSENT'), walcl = g('WALCL'), bei = g('T10YIEM'), hy = g('BAMLH0A0HYM2'),
        icsa = g('ICSA'), pce = g('PCEPILFE'), m2 = g('M2SL'),
        dfii10 = g('DFII10'), dgs30 = g('DGS30'), dswp30 = g('DSWP30'), brent = g('DCOILBRENTEU');

  const macro: Record<string, unknown> = {};
  macro.fed_funds_rate           = lat(fed);
  macro.fed_funds_rate_prev      = prv(fed);
  macro.yield_10y_fred           = lat(d10);
  macro.yield_10y_chg_bp         = lat(d10) != null && prvValid(d10) != null
    ? Math.round((lat(d10)! - prvValid(d10)!) * 100)
    : null;
  macro.yield_2y                 = lat(d2);
  macro.yield_spread_2s10s       = lat(sp);
  macro.yield_curve              = lat(sp) != null
    ? (lat(sp)! > 0 ? 'NORMAL' : lat(sp)! < -0.5 ? 'INVERTED' : 'FLAT')
    : null;
  macro.cpi_yoy                  = yoy(cpi);
  macro.cpi_latest               = lat(cpi);
  macro.core_cpi_yoy             = yoy(ccpi);
  macro.unemployment             = lat(unemp);
  macro.unemployment_prev        = prv(unemp);
  macro.gdp_level_b              = lat(gdp);
  macro.gdp_qoq                  = lat(gdp) != null && prv(gdp) != null && prv(gdp) !== 0
    ? +(((lat(gdp)! - prv(gdp)!) / prv(gdp)!) * 100).toFixed(2)
    : null;
  macro.consumer_sentiment       = lat(sent);
  macro.fed_balance_sheet_b      = lat(walcl) != null ? Math.round(lat(walcl)! / 1000) : null;
  macro.fed_balance_chg_b        = lat(walcl) != null && prv(walcl) != null
    ? Math.round((lat(walcl)! - prv(walcl)!) / 1000)
    : null;
  macro.breakeven_inflation_10y  = lat(bei);
  macro.hy_spread_bps            = lat(hy) != null ? Math.round(lat(hy)! * 100) : null;
  macro.hy_spread_chg_bps        = lat(hy) != null && prv(hy) != null
    ? Math.round((lat(hy)! - prv(hy)!) * 100)
    : null;
  macro.hy_spread_regime         = macro.hy_spread_bps != null
    ? ((macro.hy_spread_bps as number) < 300 ? 'BENIGN'
       : (macro.hy_spread_bps as number) < 500 ? 'CAUTION' : 'STRESS')
    : null;
  macro.initial_claims           = lat(icsa);
  macro.initial_claims_prev      = prv(icsa);
  macro.core_pce_yoy             = yoy(pce);
  macro.m2_yoy_pct               = yoy(m2);
  macro.m2_latest_b              = lat(m2);
  macro.tips_10y_real_yield      = lat(dfii10);
  macro.yield_30y                = lat(dgs30);
  macro.swap_30y                 = lat(dswp30);
  macro.brent_crude              = lat(brent);
  return macro;
}
